import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, MenuItem, Alert, CircularProgress, Grid } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, BusinessCenter as OfficeIssueIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ModuleHub from '../components/ModuleHub';
import { getPurchaseOrders, createPurchaseOrder, updatePurchaseOrder, getVendors } from '../../../services/api';

const PurchaseOrders = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ vendor_id: '', order_date: new Date().toISOString().split('T')[0], expected_date: '', total_amount: 0, notes: '' });

  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    try {
      setLoading(true);
      const [po, v] = await Promise.all([getPurchaseOrders(), getVendors()]);
      setItems(po.data || []);
      setVendors(v.data || []);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const statusColor = { draft: 'default', pending: 'warning', approved: 'info', received: 'success', cancelled: 'error' };

  return (
    <ModuleHub moduleId="accounts-payable" showFeatures={false}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<OfficeIssueIcon />}
          onClick={() => navigate('/office-issue')}
        >
          Issue Sun Systems Office
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>Create PO</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}><Table>
          <TableHead><TableRow><TableCell>PO #</TableCell><TableCell>Vendor</TableCell><TableCell>Order Date</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
          <TableBody>{items.map(po => (
            <TableRow key={po.id}>
              <TableCell><strong>{po.po_number}</strong></TableCell>
              <TableCell>{po.vendor?.name}</TableCell>
              <TableCell>{po.order_date}</TableCell>
              <TableCell align="right">{parseFloat(po.total_amount).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' })}</TableCell>
              <TableCell><Chip label={po.status} size="small" color={statusColor[po.status] || 'default'} /></TableCell>
              <TableCell>
                {po.status === 'draft' && <Button size="small" onClick={() => updatePurchaseOrder(po.id, { status: 'approved' }).then(loadData)}>Approve</Button>}
                {po.status === 'approved' && <Button size="small" onClick={() => updatePurchaseOrder(po.id, { status: 'received' }).then(loadData)}>Receive</Button>}
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></TableContainer>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Purchase Order</DialogTitle>
        <DialogContent><Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}><TextField fullWidth select label="Vendor" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>{vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}</TextField></Grid>
          <Grid item xs={6}><TextField fullWidth type="date" label="Order Date" InputLabelProps={{ shrink: true }} value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth type="date" label="Expected Date" InputLabelProps={{ shrink: true }} value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></Grid>
          <Grid item xs={12}><TextField fullWidth type="number" label="Total Amount" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} /></Grid>
        </Grid></DialogContent>
        <DialogActions><Button onClick={() => setDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={async () => { await createPurchaseOrder({ ...form, vendor_id: parseInt(form.vendor_id), total_amount: parseFloat(form.total_amount) }); setDialogOpen(false); loadData(); }}>Save</Button></DialogActions>
      </Dialog>
    </ModuleHub>
  );
};

export default PurchaseOrders;
